import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Disc3, Play, Pause, Loader2 } from "lucide-react";
import { RemixDownloadDialog } from "./RemixDownloadDialog";
import { usePreviewPlayer, previewSrc } from "./usePreviewPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const remixArtworkSrc = (remix) =>
  remix.artwork_url || (remix.artwork_path ? `${API}/remixes/${remix.id}/artwork` : "");

export const remixShareUrl = (remix) => `${window.location.origin}/remix/${remix.slug || remix.id}`;

export const RemixesSection = () => {
  const [remixes, setRemixes] = useState([]);
  const [selected, setSelected] = useState(null);
  const player = usePreviewPlayer();
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/remixes`)
      .then((res) => setRemixes(res.data))
      .catch((err) => console.error("Failed to fetch remixes:", err));
  }, []);

  useEffect(() => {
    if (!slug || remixes.length === 0) return;
    const match = remixes.find((r) => r.slug === slug || r.id === slug);
    setTimeout(() => {
      const el = document.querySelector("#remixes");
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: "auto" });
    }, 150);
    if (match) setSelected(match);
    else toast.error("That remix isn't available anymore.");
  }, [slug, remixes]);

  const handleDialogChange = (open) => {
    if (open) return;
    setSelected(null);
    if (slug) navigate("/", { replace: true });
  };

  return (
    <section id="remixes" data-testid="remixes-section" className="py-24 md:py-32 tracing-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <p className="font-sub text-[10px] text-white/30 tracking-[0.3em] mb-4">006</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter">REMIXES & BOOTLEGS</h2>
          <div className="gold-line mt-6" />
          <p className="font-body text-white/40 text-sm mt-6 max-w-xl">
            Free downloads for DJs and fans. Click a track, drop your email, and the file is yours.
          </p>
        </div>

        {remixes.length === 0 ? (
          <p data-testid="remixes-empty" className="font-sub text-xs text-white/30 tracking-widest">
            NEW EDITS COMING SOON
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {remixes.map((remix, i) => (
              <RemixTile
                key={remix.id}
                remix={remix}
                index={i}
                onSelect={() => setSelected(remix)}
                player={player}
              />
            ))}
          </div>
        )}
      </div>

      <RemixDownloadDialog remix={selected} onOpenChange={handleDialogChange} />
    </section>
  );
};

const RemixTile = ({ remix, index, onSelect, player }) => {
  const isPlaying = player.playingId === remix.id;
  const isLoading = player.loadingId === remix.id;
  const hasPreview = !!previewSrc(remix);

  return (
    <div data-testid={`remix-${index}`} className="group relative">
      <div className="relative aspect-square overflow-hidden border border-white/5 mb-3 group-hover:border-white/20 transition-colors duration-300">
        {remixArtworkSrc(remix) ? (
          <img
            src={remixArtworkSrc(remix)}
            alt={remix.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
            <Disc3 className="w-8 h-8 text-white/10" />
          </div>
        )}
        <div className="absolute inset-0 bg-[#050505]/30 group-hover:bg-[#050505]/10 transition-colors duration-300" />
        <button
          type="button"
          data-testid={`remix-open-${index}`}
          aria-label={`Download ${remix.title}`}
          onClick={onSelect}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="bg-[#050505]/80 backdrop-blur-sm border border-white/20 px-4 py-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Download className="w-3 h-3 text-white/70" />
            <span className="font-sub text-[10px] text-white tracking-widest">FREE DOWNLOAD</span>
          </div>
        </button>
        <div className="absolute top-2 left-2 pointer-events-none">
          <span className="font-sub text-[8px] tracking-widest bg-white/10 border border-white/20 text-white px-2 py-0.5 uppercase">
            {remix.remix_type}
          </span>
        </div>
        {hasPreview && (
          <button
            type="button"
            data-testid={`remix-preview-btn-${index}`}
            aria-label={isPlaying ? "Pause preview" : "Play 30-second preview"}
            title={isPlaying ? "Pause preview" : "Play 30s preview"}
            onClick={() => player.toggle(remix)}
            className={`absolute right-2 bottom-3 w-10 h-10 rounded-full border flex items-center justify-center backdrop-blur-md transition-colors duration-300 ${
              isPlaying
                ? "bg-white text-black border-white"
                : "bg-[#050505]/70 text-white border-white/30 hover:bg-white hover:text-black hover:border-white"
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-4 h-4" fill="currentColor" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
            )}
          </button>
        )}
        {isPlaying && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 pointer-events-none">
            <div
              data-testid={`remix-preview-progress-${index}`}
              className="h-full bg-white transition-[width] duration-200 ease-linear"
              style={{ width: `${Math.round(player.progress * 100)}%` }}
            />
          </div>
        )}
      </div>
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <h3 className="font-heading text-sm tracking-tight text-white group-hover:text-white/80 leading-snug line-clamp-2">
          {remix.title}
        </h3>
        {remix.original_artist && (
          <p className="font-body text-[11px] text-white/30 truncate">{remix.original_artist}</p>
        )}
        <p className="font-sub text-[9px] text-white/25 tracking-widest mt-1 uppercase">
          Alchemist {remix.remix_type}{hasPreview ? " · 30s preview" : ""}
        </p>
      </button>
    </div>
  );
};
